import { useCallback, useState } from 'react'

import { useNavigation } from '@/hooks/useNavigation'
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'

import { useDisclosure } from '@chakra-ui/react'

import { Box as DarkBox } from '@hivewatch/dark'

import { DarkEditFloorModal } from '@/features/floor-crud'

import { FloorI } from '../../types/types'
import { DarkFloorListItem } from './DarkFloorListItem'
import { darkFloorsPanelStyles } from './DarkFloorsPanel.styles'

interface DarkFloorListIProps {
  colorScheme: 'dark' | 'light'
  currentFloorId?: string
  floors: FloorI[]
  canReorderFloors?: boolean
  isEditEnabled?: boolean
  onReorderFloors?: (floorIds: string[]) => void
}

export const DarkFloorList = ({
  colorScheme,
  currentFloorId,
  floors,
  canReorderFloors = false,
  isEditEnabled = false,
  onReorderFloors,
}: DarkFloorListIProps) => {
  const { t } = useTranslation()
  const { goToFloorPlan } = useNavigation()
  const {
    isOpen: isEditFloorOpen,
    onClose: onEditFloorClose,
    onOpen: onEditFloorOpen,
  } = useDisclosure()
  const [editedFloorId, setEditedFloorId] = useState<string | null>(null)

  const onEditButtonClick = (floorId: string) => {
    setEditedFloorId(floorId)
    onEditFloorOpen()
  }

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination || !floors || !onReorderFloors) return

      const items = Array.from(floors)
      const [reorderedItem] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, reorderedItem)

      onReorderFloors(items.map((item) => item.id))
    },
    [floors, onReorderFloors]
  )

  const s = darkFloorsPanelStyles(colorScheme)

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='dark-floor-list'>
          {(provided) => (
            <DarkBox
              {...s.list}
              data-testid='floorPlanPage_darkFloorsPanel_list'
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {floors?.map((floor, index) => (
                <Draggable
                  draggableId={floor.id}
                  index={index}
                  isDragDisabled={!canReorderFloors}
                  key={floor.id}
                >
                  {(dragProvided) => (
                    <DarkBox
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                    >
                      <DarkFloorListItem
                        colorScheme={colorScheme}
                        dragHandleProps={
                          canReorderFloors ? dragProvided.dragHandleProps : null
                        }
                        editButtonAriaLabel={t(
                          'floors:floors_panel_edit_floor_aria'
                        )}
                        hasIncidents={floor.hasIncidents}
                        isCurrentFloor={currentFloorId === floor.id}
                        isDefaultFloor={floor.isDefaultFloor}
                        isEditEnabled={isEditEnabled}
                        label={floor.label}
                        onClick={() => goToFloorPlan(floor.id)}
                        onEditButtonClick={() => onEditButtonClick(floor.id)}
                      />
                    </DarkBox>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </DarkBox>
          )}
        </Droppable>
      </DragDropContext>
      {isEditFloorOpen && (
        <DarkEditFloorModal
          floorId={editedFloorId}
          isOpen={isEditFloorOpen}
          onClose={onEditFloorClose}
        />
      )}
    </>
  )
}
